import styled from '@emotion/styled';
import React, { FC, useCallback } from 'react';
import { HackathonStage } from '../../Api';
import { Button } from '../shared/Button';
import theme from '../shared/theme';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

type Props = { currentStage: HackathonStage; setStage(stage: HackathonStage): void };

export const HackathonStageEditor: FC<Props> = ({ currentStage, setStage }) => {
  const availableStages = Object.keys(HackathonStage);
  const selectedStage = availableStages.indexOf(currentStage);
  const previousStage = availableStages[selectedStage - 1] as HackathonStage;
  const nextStage = availableStages[selectedStage + 1] as HackathonStage;

  const nextStageText = nextStage === HackathonStage.Finished ? 'Mark as' : 'Start';

  const back = useCallback(() => setStage(previousStage), [previousStage, setStage]);
  const next = useCallback(() => setStage(nextStage), [nextStage, setStage]);
  
  return (
    <Card>
      <SectionTitle>Hackathon Stage</SectionTitle>
      <StageContainer>
        {previousStage && (
          <StageButton onClick={back} size="lg">
            <FontAwesomeIcon icon={faChevronLeft} />
            <ButtonContent>
              <ButtonLabel>Back to</ButtonLabel>
              <StageName>{previousStage}</StageName>
            </ButtonContent>
          </StageButton>
        )}
        
        <CurrentStageDisplay>
          <CurrentStageName>{currentStage}</CurrentStageName>
          <CurrentStageLabel>Current Stage</CurrentStageLabel>
        </CurrentStageDisplay>
        
        {nextStage && (
          <StageButton onClick={next} category="primary" size="lg">
            <ButtonContent>
              <ButtonLabel>{nextStageText}</ButtonLabel>
              <StageName>{nextStage}</StageName>
            </ButtonContent>
            <FontAwesomeIcon icon={faChevronRight} />
          </StageButton>
        )}
      </StageContainer>
    </Card>
  );
};

const Card = styled.div`
  ${theme.styles.materialPanel};
  background: #fff;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin: 0 0 15px 0;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.blue};
  border-bottom: 2px solid ${theme.colors.borderGrey};
  padding-bottom: 10px;
`;

const StageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: stretch;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: stretch;
    gap: 15px;
  }
`;

const StageButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex: 1;
  padding: 15px 20px;
  font-size: 14px;

  @media (min-width: 768px) {
    flex: 1;
    max-width: 280px;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const ButtonLabel = styled.div`
  font-size: 11px;
  opacity: 0.75;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 400;
`;

const StageName = styled.div`
  font-weight: 600;
  font-size: 15px;
`;

const CurrentStageDisplay = styled.div`
  ${theme.styles.materialPanel};
  background: #fff;
  border: 2px solid ${theme.colors.borderGrey};
  text-align: center;
  padding: 25px 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;

  @media (min-width: 768px) {
    flex: 1;
    min-width: 200px;
  }
`;

const CurrentStageName = styled.div`
  font-size: 24px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: ${theme.colors.blue};
`;

const CurrentStageLabel = styled.div`
  font-size: 11px;
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 400;
  color: #666;
`;
